import { spaceObjectInvalidations } from '@entities/SpaceObject/queries';
import { UserRequest } from '@entities/User/schema';
import { UserFront } from '@entities/User/types';
import { ENTITIES } from '@entities/enum';
import { SortDirection } from '@features/ui-filters/api/filterUtils';
import { FilterGroup } from '@features/ui-filters/components/types';
import { userApi } from '@services/api/endpoints/user';
import { ACTIONS, PaginatedResponse } from '@root/src/types/common';
import { infiniteQueryOptions, mutationOptions, QueryClient, QueryKey, queryOptions } from '@tanstack/react-query';

/**
 * Users in the React Query cache: under which key each user read lives, the current user
 * included, and which reads each user write leaves stale. Components and hooks read and
 * write users through this module only; no user key is spelled anywhere else.
 */

/** A page of `GET /users`, exactly as it is requested: the key holds what the fetch sends. */
export type UserListParams = {
  page?: number;
  pageSize?: number;
  filters?: FilterGroup;
  sort?: { field: string; order: SortDirection }[];
  includePocStats?: boolean;
  pocPreviewLimit?: number;
};

/** The user pickers scroll through `GET /users` page by page; the page is the scroll's. */
export type UserInfiniteListParams = Omit<UserListParams, 'page'>;

/**
 * Every key starts with `userKeys.all`, then names the resource. A write invalidates a
 * resource scope (`me()`, `detail(id)`, `lists()`), which prefix-matches every read of that
 * resource and nothing else:
 * - `me()` is the current user (`GET /users/me`): the session. One entry, whoever reads it;
 * - `lists()` is every read of several users: the contacts directory, a member's points of
 *   contact, users and point-of-contact count, and the user pickers' infinite scroll;
 * - `detail(id)` is one user (`GET /users/:id`), the current user's own page included.
 */
export const userKeys = {
  all: ['users'] as const,
  me: () => [...userKeys.all, 'me'] as const,
  lists: () => [...userKeys.all, 'list'] as const,
  list: (params: UserListParams) => [...userKeys.lists(), params] as const,
  infiniteList: (params: UserInfiniteListParams) => [...userKeys.lists(), 'infinite', params] as const,
  detail: (userId: string) => [...userKeys.all, 'detail', userId] as const,
};

const THIRTY_SECONDS = 30 * 1000;
const ONE_MINUTE = 60 * 1000;

const getUsers = ({ page, pageSize, filters, sort, includePocStats, pocPreviewLimit }: UserListParams) =>
  userApi.getUsers(filters ?? {}, page, pageSize, sort, { includePocStats, pocPreviewLimit });

/**
 * Every user read. What belongs to the read itself lives here: key, fetch, stale time, the
 * session's retry and refetch policy, and `enabled` only as "no id, no fetch". A caller that
 * adds its own `enabled` flag, retry or refetch policy spreads these options and keeps it.
 */
export const userQueries = {
  /**
   * The current user. It is the session: the logged-in layout shows a spinner until it
   * answers, and the middleware redirects a logged-out visitor, so it is read once and never
   * refired on its own. With an HttpOnly cookie the client cannot tell whether it is logged
   * in, so the read is always enabled; an expired session answers 401, which the global
   * error handler sends to /login without a toast (the layout's spinner covers errors).
   */
  me: () =>
    queryOptions<UserFront | undefined, Error>({
      queryKey: userKeys.me(),
      queryFn: () => userApi.me(),
      retry: false,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      meta: { skipErrorToast: true },
    }),
  /** One user. Its stale time and refetch policy are the caller's. */
  detail: (userId: string) =>
    queryOptions({
      queryKey: userKeys.detail(userId),
      queryFn: () => userApi.getUser(userId),
      enabled: !!userId,
    }),
  /** Every list page is read with a 30 s stale time. */
  list: (params: UserListParams) =>
    queryOptions({
      queryKey: userKeys.list(params),
      queryFn: () => getUsers(params),
      staleTime: THIRTY_SECONDS,
    }),
  /**
   * How many users match: a one-item page, read for its `total` (a member's points of contact
   * tab badge). It is a list page, so every write that refreshes the lists refreshes it.
   */
  count: (filters: FilterGroup) =>
    queryOptions({
      queryKey: userKeys.list({ page: 1, pageSize: 1, filters }),
      queryFn: () => getUsers({ page: 1, pageSize: 1, filters }),
      staleTime: ONE_MINUTE,
    }),
  /** The user pickers: `GET /users` page by page, as far as the user scrolls. */
  infiniteList: (params: UserInfiniteListParams) =>
    infiniteQueryOptions({
      queryKey: userKeys.infiniteList(params),
      initialPageParam: 1,
      queryFn: ({ pageParam }) => getUsers({ ...params, page: pageParam }),
      getNextPageParam: (lastPage: PaginatedResponse<UserFront>, allPages: PaginatedResponse<UserFront>[]) =>
        allPages.length < lastPage.pages ? allPages.length + 1 : undefined,
      staleTime: THIRTY_SECONDS,
    }),
};

const invalidate = async (queryClient: QueryClient, keys: QueryKey[]): Promise<void> => {
  await Promise.all(keys.map(queryKey => queryClient.invalidateQueries({ queryKey })));
};

/**
 * What each user write leaves stale: the one place a user write's refresh targets are
 * declared. The mutations below call them, and `useLogin` calls `loggedIn`.
 *
 * A user save also refreshes the profile's "my space objects" list, which shows its point of
 * contact (`spaceObjectInvalidations.userSaved`). No other entity's write refreshes users.
 */
export const userInvalidations = {
  /** The session is the user the login answered: the logged-in pages read it without a request. */
  loggedIn: (queryClient: QueryClient, user: UserFront) => {
    queryClient.setQueryData(userKeys.me(), user);
  },
  /** The new user shows in the lists, and their page opens from the saved response. */
  created: (queryClient: QueryClient, user: UserFront) => {
    queryClient.setQueryData(userKeys.detail(user.id), user);
    return Promise.all([invalidate(queryClient, [userKeys.lists()]), spaceObjectInvalidations.userSaved(queryClient)]);
  },
  updated: (queryClient: QueryClient, userId: string) =>
    Promise.all([
      invalidate(queryClient, [userKeys.detail(userId), userKeys.lists()]),
      spaceObjectInvalidations.userSaved(queryClient),
    ]),
  /** Saving oneself: the session too, and one's own page (`GET /users/:id`). */
  meUpdated: (queryClient: QueryClient, userId: string) =>
    Promise.all([
      invalidate(queryClient, [userKeys.me(), userKeys.detail(userId), userKeys.lists()]),
      spaceObjectInvalidations.userSaved(queryClient),
    ]),
  /** The deleted user leaves the lists. Their own page and bylines keep them until they refetch. */
  deleted: (queryClient: QueryClient) => invalidate(queryClient, [userKeys.lists()]),
};

/**
 * Every user write. Each refreshes through `userInvalidations`, on the client that ran it
 * (`useMutation`'s own client, handed to `onSuccess`), so `useMutation(userMutations.x())`
 * needs no `useQueryClient`.
 *
 * The refresh is requested, not awaited: the form closes (and opens the saved user's page)
 * as soon as the save answers, as it always has.
 */
export const userMutations = {
  create: () =>
    mutationOptions<UserFront, Error, UserRequest>({
      mutationFn: request => userApi.createUser(request),
      meta: { entity: ENTITIES.USER, action: ACTIONS.CREATE },
      retry: false,
      onSuccess: (user, _, __, { client }) => {
        void userInvalidations.created(client, user);
      },
    }),
  /** Another user (`PUT /users/:id`). Its variables carry the id, as `useUpdateUser` always took it. */
  update: () =>
    mutationOptions<UserFront, Error, { data: UserRequest; id: string }>({
      mutationFn: ({ data, id }) => userApi.updateUser(data, id),
      meta: { entity: ENTITIES.USER, action: ACTIONS.UPDATE },
      onSuccess: (_, { id }, __, { client }) => {
        void userInvalidations.updated(client, id);
      },
    }),
  /** Oneself (`PUT /users/me`). */
  updateMe: () =>
    mutationOptions<UserFront, Error, UserRequest>({
      mutationFn: request => userApi.updateMe(request),
      meta: { entity: ENTITIES.USER, action: ACTIONS.UPDATE },
      onSuccess: (me, _, __, { client }) => {
        void userInvalidations.meUpdated(client, me.id);
      },
    }),
  delete: () =>
    mutationOptions<void, Error, string>({
      mutationFn: userId => userApi.deleteUser(userId),
      meta: { entity: ENTITIES.USER, action: ACTIONS.DELETE },
      onSuccess: (_, __, ___, { client }) => {
        void userInvalidations.deleted(client);
      },
    }),
};
